import React, { useId } from 'react';
import './TickSliderAtom.scss';

export interface TickSliderAtomProps {
  /** Current value (clamped to [min, max]). */
  value: number;
  /** Minimum (inclusive). Default 1. */
  min?: number;
  /** Maximum (inclusive). */
  max: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * Discrete tick slider (min…max, step 1) — a filled gradient track with a tick
 * dot at every stop and a raised handle at the current value. Design source:
 * context-selector `editor.jsx` "Repeat this walk" control.
 *
 * A native `<input type="range">` is layered transparently over the visual track
 * so keyboard (arrows/Home/End), focus, drag and ARIA (`role="slider"`,
 * `aria-valuenow`) come for free; the painted layer is `aria-hidden`.
 */
const TickSliderAtom: React.FC<TickSliderAtomProps> = ({
  value,
  min = 1,
  max,
  onChange,
  disabled = false,
  ariaLabel,
  className = '',
  'data-testid': testId,
}) => {
  const id = useId();
  const clamped = Math.min(max, Math.max(min, value));
  const span = Math.max(1, max - min);
  const fillPct = ((clamped - min) / span) * 100;
  const ticks = Array.from({ length: max - min + 1 }, (_, i) => min + i);

  return (
    <div
      className={`tick-slider-atom${disabled ? ' tick-slider-atom--disabled' : ''} ${className}`.trim()}
      data-testid={testId}
    >
      <div className="tick-slider-atom__track" aria-hidden="true">
        <span className="tick-slider-atom__fill" style={{ width: `${fillPct}%` }} />
        {ticks.map((n) => (
          <span
            key={n}
            className={`tick-slider-atom__tick${n <= clamped ? ' tick-slider-atom__tick--on' : ''}${n === clamped ? ' tick-slider-atom__tick--current' : ''}`}
            style={{ left: `${((n - min) / span) * 100}%` }}
          />
        ))}
      </div>
      <input
        id={id}
        type="range"
        className="tick-slider-atom__input"
        min={min}
        max={max}
        step={1}
        value={clamped}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={disabled}
        aria-label={ariaLabel}
      />
    </div>
  );
};

TickSliderAtom.displayName = 'TickSliderAtom';

export default TickSliderAtom;
